import React from 'react'
import { match } from 'ts-pattern'
import { FilterSchema } from '@adc/blueprint'
import type { PanelHeight } from '../types'
import Text from '../../text'
import SingleSelect from '../components/singleSelect'
import MultiSelect from '../components/multiSelect'
import Range from '../components/range'
import Boolean from '../components/boolean'
import classnames from 'classnames/bind'
import styles from './styles.scss'
const cx = classnames.bind(styles)

const renderControl = (
  filterDefinition: FilterSchema.FilterDefinition,
  elmId: string,
  onChange: (key: string, value: FilterSchema.ValueType) => void,
  value: FilterSchema.ValueType
) => {
  const controlType: FilterSchema.ControlType = filterDefinition.controlType
  return match(controlType)
    .with('singleSelect', () => (
      <SingleSelect
        value={value as FilterSchema.SingleSelectValueType}
        onChange={onChange}
        elmId={elmId}
        definition={
          filterDefinition as FilterSchema.FilterSingleSelectDefinition
        }
      />
    ))
    .with('multiSelect', () => (
      <MultiSelect
        value={value as FilterSchema.MultiSelectValueType}
        onChange={onChange}
        elmId={elmId}
        definition={
          filterDefinition as FilterSchema.FilterMultiSelectDefinition
        }
      />
    ))
    .with('range', () => (
      <Range
        value={
          value as FilterSchema.RangeValueType<FilterSchema.RangeControlValueType>
        }
        onChange={onChange}
        elmId={elmId}
        definition={filterDefinition as FilterSchema.FilterRangeDefinition}
      />
    ))
    .with('boolean', () => (
      <Boolean
        value={value as FilterSchema.BooleanValueType}
        onChange={onChange}
        elmId={elmId}
        definition={filterDefinition as FilterSchema.FilterBooleanDefinition}
      />
    ))
    .otherwise(() => {
      throw new Error('Invalid control type')
    })
}

type SingleFilterProps = {
  elmId?: string
  filterDefinition: FilterSchema.FilterDefinition
  value: FilterSchema.ValueType
  onChange: (key: string, value: FilterSchema.ValueType) => void
}

const SingleFilterLayout: React.FunctionComponent<SingleFilterProps> = ({
  elmId = 'filter_control',
  filterDefinition,
  value,
  onChange,
}) => {
  return (
    <div
      data-elm-id={elmId}
      className={cx('filter-layout', {
        half: filterDefinition.styleIntent?.width === 'half',
      })}
    >
      {/* boolean control we'll handle label and description in the control */}
      {filterDefinition.controlType !== 'boolean' && (
        <div className={cx('label-and-description')}>
          <h4 className={cx('filter-label')}>
            {filterDefinition.label && filterDefinition.label}
          </h4>
          {filterDefinition.description && (
            <Text color='gray' size='tiny' className={cx('description')}>
              {filterDefinition.description}
            </Text>
          )}
        </div>
      )}
      <div className={cx('single-filter')}>
        {renderControl(filterDefinition, elmId, onChange, value)}
      </div>
    </div>
  )
}

type FilterGroupLayoutProps = {
  elmId?: string
  groupDefinition: FilterSchema.FilterGroupDefinition
  groupData: FilterSchema.FilterGroupViewModel
  onChange: (
    // modelName: string,
    key: string,
    value: FilterSchema.ValueType
  ) => void
  height?: PanelHeight
}

/**
 * Renders a group of filters
 * @param groupDefinition: the definition for the group
 * @param groupData: the data for the group
 */
const FilterGroupLayout: React.FunctionComponent<FilterGroupLayoutProps> = ({
  elmId = 'filter_panel',
  groupDefinition,
  groupData,
  onChange,
  height,
}) => {
  const onChangeFilter = (key: string, newValue: FilterSchema.ValueType) => {
    onChange(key, newValue)
  }
  return (
    <div
      data-elm-id={elmId}
      className={cx('layout', { 'full-height': height === 'max' })}
    >
      <div className={cx('header')}>
        <h2>{groupDefinition.label && groupDefinition.label}</h2>
        {groupDefinition.description && (
          <Text color='gray' size='tiny'>
            {groupDefinition.description}
          </Text>
        )}
        {groupDefinition.label && groupDefinition.description && <hr />}
      </div>
      <div className={cx('filter-layout-items')}>
        {groupDefinition.items.map((item, index) => {
          if (item.itemType === 'filter') {
            const filterDefinition = item as FilterSchema.FilterDefinition
            const name = filterDefinition.name
            return (
              <SingleFilterLayout
                key={index}
                filterDefinition={filterDefinition}
                value={groupData[name]}
                onChange={onChangeFilter}
              />
            )
          } else if (item.itemType === 'section') {
            // section rendering
            const sectionDefinition =
              item as FilterSchema.FilterSectionDefinition
            return (
              <div key={index} className={cx('section')}>
                <h3>{sectionDefinition.label}</h3>
                {sectionDefinition.description && (
                  <Text color='gray' size='tiny'>
                    {sectionDefinition.description}
                  </Text>
                )}
                {sectionDefinition.label && sectionDefinition.description && (
                  <hr />
                )}
                {sectionDefinition.filters.map((item, index) => {
                  const filterDefinition = item
                  const name = filterDefinition.name
                  return (
                    <SingleFilterLayout
                      key={index}
                      filterDefinition={filterDefinition}
                      value={groupData[name]}
                      onChange={onChangeFilter}
                    />
                  )
                })}
              </div>
            )
          } else {
            throw new Error(
              `Invalid group item type: ${groupDefinition.itemType}.  Expected 'filter' or 'section'.`
            )
          }
        })}
      </div>
    </div>
  )
}

export default FilterGroupLayout
